package com.appsyde.shared.presentation.screen.component.message

import androidx.compose.runtime.Composable
import com.appsyde.shared.app.presentation.ticket.HistoryState
import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2
import com.appsyde.shared.presentation.screen.component.message.helper.byKey
import com.appsyde.shared.presentation.screen.component.message.helper.computeHeaderTitle
import com.appsyde.shared.presentation.screen.component.message.helper.intentFamily
import com.appsyde.shared.presentation.screen.component.message.helper.intentLabelFr
import com.appsyde.shared.presentation.style.AppStylesTicketHeader
import com.appsyde.shared.presentation.style.style.AppStylesCards
import com.appsyde.shared.presentation.style.style.MainTopTabsStyles
import com.appsyde.shared.modules.ticket.domain.model.TicketStatus
import org.jetbrains.compose.web.dom.*

enum class TopMode { RESPOND, TICKETS, ACCOUNT }

@Composable
fun TopModeTabs(
    mode: TopMode,
    onChange: (TopMode) -> Unit
) {
    Div(attrs = { classes(MainTopTabsStyles.tabsWrap) }) {

        Button(attrs = {
            classes(MainTopTabsStyles.tabButton)
            if (mode == TopMode.TICKETS) classes(MainTopTabsStyles.tabButtonActive)
            onClick { onChange(TopMode.TICKETS) }
            attr("type", "button")
        }) {
            Text("Tickets")
            if (mode == TopMode.TICKETS) {
                Span(attrs = { classes(MainTopTabsStyles.tabActiveDot) })
            }
        }

        Button(attrs = {
            classes(MainTopTabsStyles.tabButton)
            if (mode == TopMode.RESPOND) classes(MainTopTabsStyles.tabButtonActive)
            onClick { onChange(TopMode.RESPOND) }
            attr("type", "button")
        }) {
            Text("Répondre")
            if (mode == TopMode.RESPOND) {
                Span(attrs = { classes(MainTopTabsStyles.tabActiveDot) })
            }
        }
    }
}

enum class TicketWorkflowStatus {
    OPEN,
    WAITING,
    CLOSED;

    fun labelFr(): String = when (this) {
        OPEN -> "Ouvert"
        WAITING -> "En attente"
        CLOSED -> "Fermé"
    }

    fun next(): TicketWorkflowStatus = when (this) {
        OPEN -> WAITING
        WAITING -> CLOSED
        CLOSED -> OPEN
    }
}

/** Parse ticket.status (from API) to TicketWorkflowStatus. Defaults to OPEN if unknown. */
fun String.toTicketWorkflowStatus(): TicketWorkflowStatus = when (trim().uppercase()) {
    TicketStatus.OPEN -> TicketWorkflowStatus.OPEN
    TicketStatus.WAITING -> TicketWorkflowStatus.WAITING
    TicketStatus.CLOSED -> TicketWorkflowStatus.CLOSED
    else -> TicketWorkflowStatus.OPEN
}

@Composable
fun RespondCardHeader(
    subject: String,
    currentIntent: String? = null,
    detectedFields: List<DetectedFieldV2>? = null,
    onBack: () -> Unit,
    canGoBack: Boolean,

    hasHistory: Boolean,
    historyCount: Int?,
    historyState: HistoryState,
    onToggleHistory: () -> Unit,

    status: TicketWorkflowStatus,
    onCycleStatus: () -> Unit,
    onSkip: () -> Unit = {},
) {
    val m = detectedFields.byKey()
    val family = intentFamily(currentIntent)
    val intentLabel = intentLabelFr(currentIntent)

    // ✅ Utilisation officielle
    val computedTitle = computeHeaderTitle(
        intentLabel = intentLabel,
        subject = subject,
        family = family,
        m = m
    )

    Div(attrs = { classes(AppStylesCards.ticketCardHeader) }) {

        // -------------------------
        // Left side: back + title + meta
        // -------------------------
        Div(attrs = { classes(AppStylesTicketHeader.ticketHeaderLeft) }) {

            if (canGoBack) {
                Button(attrs = {
                    classes(AppStylesTicketHeader.headerSkipButton)
                    attr("type", "button")
                    attr("aria-label", "Retour")
                    attr("title", "Retour")
                    onClick { onBack() }
                }) { Text("‹") }
            }

            Div(attrs = { classes(AppStylesTicketHeader.ticketHeaderTitleWrap) }) {

                // Title (intent + anchor)

                Div(attrs = { classes(AppStylesTicketHeader.ticketHeaderTitleRow) }) {
                    Div(attrs = { classes(AppStylesTicketHeader.ticketHeaderTitle) }) {
                        Text(computedTitle)
                    }
                }
            }
        }

        // -------------------------
        // Right side: actions + status
        // -------------------------
        Div(attrs = { classes(AppStylesTicketHeader.ticketHeaderRight) }) {
            Div(attrs = { classes(AppStylesTicketHeader.ticketHeaderActionsRow) }) {

                if (hasHistory) {
                    val isLoading = historyState is HistoryState.Loading

                    HistoryHeaderPill(
                        count = when (historyState) {
                            is HistoryState.Loaded -> historyState.items.size
                            else -> historyCount
                        },
                        isLoading = isLoading,
                        onToggle = onToggleHistory,
                        disabled = isLoading
                    )
                }

                StatusHeaderBadge(status = status, onClick = onCycleStatus)

                Button(attrs = {
                    classes(AppStylesTicketHeader.headerSkipButton)
                    attr("type", "button")
                    attr("aria-label", "Ticket suivant")
                    attr("title", "Ticket suivant")
                    onClick { onSkip() }
                }) { Text("›") }
            }
        }
    }
}


    @Composable
    private fun HistoryHeaderPill(
        count: Int?,
        isLoading: Boolean,
        onToggle: () -> Unit,
        disabled: Boolean
    ) {
        val label = if (count != null) "🕘 Historique ($count)" else "🕘 Historique"
        Div(attrs = {
            classes(AppStylesTicketHeader.historyInline)
            if (disabled) classes(AppStylesTicketHeader.historyInlineDisabled)
            if (!disabled) onClick { onToggle() }
        }) {
            Span(attrs = { classes(AppStylesTicketHeader.historyInlineText) }) {
                if (isLoading) {
                    Text("🕘 Historique (…)")
                } else {
                    Text(label)
                }
            }
        }
    }

    @Composable
    private fun StatusHeaderBadge(
        status: TicketWorkflowStatus,
        onClick: () -> Unit
    ) {
        val statusClass = when (status) {
            TicketWorkflowStatus.OPEN -> AppStylesTicketHeader.headerBadgeOpen
            TicketWorkflowStatus.WAITING -> AppStylesTicketHeader.headerBadgeWaiting
            TicketWorkflowStatus.CLOSED -> AppStylesTicketHeader.headerBadgeClosed
        }

        Button(attrs = {
            classes(AppStylesTicketHeader.headerBadge, statusClass)
            onClick { onClick() }
            attr("title", "Changer le statut")
            attr("aria-label", "Statut : ${status.labelFr()}. Cliquer pour changer.")
            attr("type", "button")
        }) {
            Text(status.labelFr())
        }
    }


